import {
  FaActionTracker,
  StructuredLogger,
} from '@brighthub/structured-logging';
import { inject, Injectable, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { MessageService } from 'primeng/api';
import { FinancialSnapshotAnalytics } from '../analytics/financial-snapshot-analytics';
import { FINANCIAL_SNAPSHOT_API } from '../financial-snapshot-api.token';
import type { SnapshotStatus } from '../financial-snapshot.model';
import { FINANCIAL_SNAPSHOT_UI_ROW_COUNT } from '../compute/financial-snapshot-compute';

@Injectable({ providedIn: 'root' })
export class FinancialSnapshotSendAction {
  private readonly _actionLogs = new FaActionTracker(inject(StructuredLogger));
  private readonly _api = inject(FINANCIAL_SNAPSHOT_API);
  private readonly _messages = inject(MessageService);
  private readonly _analytics = inject(FinancialSnapshotAnalytics);

  readonly sending = signal(false);

  /**
   * POST /financialsnapshot/invite with the active client's guid.
   * Completeness is for Amplitude only — the lambda decides visual vs intake.
   */
  async send(params: {
    clientId: string;
    status: SnapshotStatus | null;
    enteredRowCount: number;
  }): Promise<void> {
    if (this.sending()) {
      return;
    }
    const completeness: 'complete' | 'incomplete' =
      params.status === 'complete' || params.status === 'outdated'
        ? 'complete'
        : params.enteredRowCount >= FINANCIAL_SNAPSHOT_UI_ROW_COUNT
          ? 'complete'
          : 'incomplete';

    this.sending.set(true);
    try {
      await this._actionLogs.run(
        'financial_snapshot.send',
        params.clientId,
        async (attempt) => {
          attempt.stage = 'acceptance';
          attempt.accepted = true;
          await attempt.request(
            firstValueFrom(this._api.inviteClient(params.clientId)),
          );
        },
      );
      this._analytics.sentToClient(params.clientId, completeness);
      this._messages.add({
        severity: 'success',
        summary: 'Sent',
        detail:
          'Financial snapshot link sent to the client. It may take a couple of minutes to arrive.',
      });
    } catch (err) {
      this._messages.add({
        severity: 'error',
        summary: 'Send failed',
        detail:
          err instanceof Error
            ? err.message
            : 'Could not send the snapshot to the client.',
      });
    } finally {
      this.sending.set(false);
    }
  }
}
